import { useCallback, useEffect, useState } from 'react';
import { useInterval } from 'react-use';

interface HookConfig {
    interval?: number;
    enabled?: boolean;
    executeOnEnable?: boolean;
}

export const useAsyncPolling = (callback: () => Promise<unknown>, config?: HookConfig) => {
    const { enabled = true, interval = 2000, executeOnEnable = true } = config || {};

    const [isOperating, setIsOperating] = useState(false);
    const [isFocused, setIsFocused] = useState(
        typeof document !== 'undefined' && document.hasFocus(),
    );

    /**
     * We will only enable polling when: 1) the async callback is not running, 2) the hook is enabled,
     * and 3) the window is focused.
     */
    const shouldRunInterval = enabled && !isOperating && isFocused;
    const evaluatedInterval = shouldRunInterval ? interval : null;

    /**
     * To prevent unnecessary use of user bandwidth, we will only enable polling if and only if
     * the user is focused on the window that has the feature.
     */
    useEffect(() => {
        const onBlur = () => setIsFocused(false);
        const onFocus = () => setIsFocused(true);

        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);

        return () => {
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    const execute = useCallback(() => {
        if (isOperating) return;

        setIsOperating(true);

        callback().finally(() => setIsOperating(false));
    }, [callback]);

    // If "executeOnEnable" is on, our callback will be executed everytime it gets enabled.
    useEffect(() => {
        if (enabled && executeOnEnable) execute();
    }, [enabled]);

    useInterval(execute, evaluatedInterval);

    return { isOperating };
};
